import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock, Info, Ruler, BadgeCheck, Camera, AlertTriangle, Archive, TrendingUp, Package } from "lucide-react";
import PhotoUpload from "@/components/PhotoUpload";
import CategoryName from "@/components/CategoryName";
import BumpTierPicker from "@/components/BumpTierPicker";
import {
  GENDERS, AGES, PRODUCT_TYPES, COLORS, PATTERNS, STYLES, MATERIALS, SIZES, shoeSizesFor, clothingSizesFor, garmentTypesFor, apparelDept, usesWaistLength, WAIST_SIZES, INSEAM_SIZES,
  ERAS, ERA_LABELS, AUTH_CLAIMS, PROOF_HINTS, PACKAGE_SIZES, PACKAGE_SIZE_LABELS, PACKAGE_SIZE_HINTS,
  HOME_DECOR_CATEGORIES, HOME_DECOR_CATEGORY_LABELS, HOME_DECOR_ITEMS,
  CONDITION_GRADES, GRADE_LABELS, GRADE_HINTS, measurementSpec,
  CATEGORIES, FABRIC_TYPES, FABRIC_TYPE_LABELS,
  PATTERN_WEARERS, PATTERN_CATEGORIES, PATTERN_LETTER_SIZES, PATTERN_NUMERIC_SIZES, PATTERN_DIFFICULTY, PATTERN_CLOTHING, ACCESSORY_SIZES,
  WATCH_BANDS, WATCH_BAND_LABELS, WATCH_MOVEMENTS, WATCH_MOVEMENT_LABELS, WATCH_DIAL_COLORS, WATCH_WATER_RESISTANCE, JEWELRY_TYPES, JEWELRY_METALS, JEWELRY_METAL_LABELS, JEWELRY_STONES, JEWELRY_STONE_LABELS, JEWELRY_HALLMARKS, JEWELRY_HALLMARK_LABELS, WALLET_STYLES, WALLET_STYLE_LABELS, WALLET_CARD_SLOTS, HANDBAG_STYLES, HANDBAG_STYLE_LABELS, HANDBAG_SIZES, HANDBAG_SIZE_LABELS, TIE_STYLES, TIE_STYLE_LABELS,
} from "@/lib/taxonomy";

function Field({ label, children }) {
  return (
    <div>
      <div className="label-eyebrow mb-2">{label}</div>
      {children}
    </div>
  );
}
function Select({ options, value, onChange, testid, labels }) {
  return (
    <select className="w-full hairline p-3 text-sm bg-white" value={value}
      onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map((o) => <option key={o} value={o}>{labels ? labels[o] : o}</option>)}
    </select>
  );
}
function Check({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2.5 text-sm cursor-pointer select-none py-1">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        data-testid={testid} className="w-4 h-4 accent-[#1E3A2B]" />
      <span>{label}</span>
    </label>
  );
}

export default function Sell() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { state } = useLocation();
  const prefill = Object.fromEntries(
    Object.entries(state?.prefill || {}).filter(([, v]) => v !== undefined && v !== null && v !== ""));
  const startFabric = params.get("category") === "fabric_archives" || prefill.category === "fabric_archives";
  const startHomeDecor = params.get("category") === "home_decor" || prefill.category === "home_decor";
  const [pricing, setPricing] = useState(null);
  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  const editId = params.get("edit");
  const isEdit = !!editId;
  const [form, setForm] = useState({
    title: "", description: "", price: "",
    category: startFabric ? "fabric_archives" : startHomeDecor ? "home_decor" : "marketplace",
    gender: "women", age_group: "adult",
    product_type: startFabric ? "fabric" : startHomeDecor ? "cookware" : "apparel",
    color: startFabric ? "" : "black", pattern: "solid", style: "vintage", size: "", material: "", garment_type: "", waist: "", inseam: "", difficulty: "", pattern_width: "", pattern_length: "", band: "", movement: "", dial_color: "", metal: "", stone: "", water_resistance: "", resizable: false, hallmark: "", card_slots: "",
    condition_grade: "excellent", era: "",
    measurements: {},
    ...prefill,
    image_paths: [], label_paths: [], flaw_paths: [], no_flaws: false,
    smoke_free: false, pet_free: false, odor_free: false,
    altered: false, alteration_note: "",
    brand: "", authenticity_claim: "unbranded", proof_paths: [],
    compliance_ack: true, private: false, package_size: "medium", tradeable: false,
  });
  const [saving, setSaving] = useState(false);
  const [bumpModal, setBumpModal] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setMeasure = (k, v) => setForm((f) => ({ ...f, measurements: { ...f.measurements, [k]: v } }));
  useEffect(() => {
    if (!editId) return;
    api.get(`/listings/${editId}`).then(({ data: l }) => {
      const FIELDS = ["title", "description", "category", "gender", "age_group", "product_type",
        "garment_type", "waist", "inseam", "difficulty", "pattern_width", "pattern_length",
        "band", "movement", "dial_color", "metal", "stone", "water_resistance", "resizable",
        "hallmark", "card_slots", "color", "pattern", "style", "size", "material",
        "condition_grade", "era", "brand", "authenticity_claim", "package_size",
        "smoke_free", "pet_free", "odor_free", "altered", "alteration_note", "private", "tradeable"];
      const patch = {};
      FIELDS.forEach((k) => { if (l[k] !== undefined && l[k] !== null) patch[k] = l[k]; });
      patch.price = l.price != null ? String(l.price) : "";
      patch.measurements = l.measurements || {};
      patch.image_paths = l.image_paths || [];
      patch.label_paths = l.label_paths || [];
      patch.flaw_paths = l.flaw_paths || [];
      patch.proof_paths = l.proof_paths || [];
      patch.no_flaws = !!l.no_flaws;
      patch.compliance_ack = true;
      setForm((f) => ({ ...f, ...patch }));
    }).catch(() => toast.error("Couldn't load that listing to edit."));
  }, [editId]);
  const isFabric = form.category === "fabric_archives";
  const isHomeDecor = form.category === "home_decor";
  const isPattern = isFabric && form.product_type === "pattern";
  const isWatches = form.product_type === "watches";
  const isJewelry = JEWELRY_TYPES.includes(form.product_type);
  const isWallets = form.product_type === "wallets";
  const isHandbags = form.product_type === "handbags";
  const isTies = form.product_type === "ties";
  const changeCategory = (cat) =>
    setForm((f) => ({ ...f, category: cat,
      product_type: cat === "fabric_archives" ? "fabric" : cat === "home_decor" ? "cookware" : "apparel",
      gender: cat === "home_decor" ? "unisex" : f.gender, age_group: cat === "home_decor" ? "adult" : f.age_group,
      authenticity_claim: cat === "home_decor" ? "unbranded" : f.authenticity_claim,
      garment_type: "", size: "", waist: "", inseam: "",
      era: f.era || "",
      color: cat === "fabric_archives" ? "" : (f.color || "black"), measurements: {} }));
  const claim = form.authenticity_claim;
  const brandLocked = claim === "unbranded";
  const priceNum = parseFloat(form.price) || 0;
  const spec = measurementSpec(form.product_type, form.garment_type);
  const authFee = (() => {
    if (!pricing || claim !== "authentic") return 0;
    const t = pricing.authentication_fees.find((x) => priceNum >= x.from && (x.to === null || priceNum < x.to));
    return t ? t.fee : 0;
  })();
  const replicaOverCap = pricing && claim === "replica" && priceNum > pricing.replica_price_cap;

  const bumpBoost = pricing?.boost || { home_bump_price: 2.0, home_bump_hours: 5 };
  const skipBump = () => nav(`/product/${bumpModal.id}`);

  const submit = async (e) => {
    e.preventDefault();
    if (form.image_paths.length < 3) { toast.error("Add at least 3 photos (front, back and detail shots)"); return; }
    if (!form.no_flaws && form.flaw_paths.length < 1) {
      toast.error("Add a flaw close-up, or tick 'No visible flaws'"); return;
    }
    if (claim !== "unbranded" && !form.brand.trim()) {
      toast.error(claim === "authentic" ? "Add the brand name" : "Tell buyers which brand this replica resembles");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        brand: (brandLocked && form.category !== "fabric_archives") ? "" : form.brand.trim(),
        gender: (isFabric && form.product_type !== "pattern") ? "unisex" : form.gender,
        style: isFabric ? "" : form.style,
        price: parseFloat(form.price),
      };
      if (isEdit) {
        const { data } = await api.put(`/listings/${editId}`, payload);
        if (data.held) {
          toast.error(
            `Held for safety review: your listing mentions possible restricted/hazardous items (${(data.hazard_terms || []).join(", ")}). It won't go live until reviewed.`,
            { duration: 9000 });
        } else {
          toast.success("Changes saved");
        }
        nav(`/product/${editId}`);
        return;
      }
      const { data } = await api.post("/listings", payload);
      toast.success("Listing published");
      if (data.held) {
        toast.error(
          `Held for safety review: your listing mentions possible restricted/hazardous items (${(data.hazard_terms || []).join(", ")}). It won't go live until reviewed — contact support if this is a mistake.`,
          { duration: 9000 }
        );
        nav(`/product/${data.id}`);
        return;
      }
      setBumpModal({ id: data.id, price: parseFloat(form.price) || 0 });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="sell-page" className="max-w-[1000px] mx-auto px-6 py-12">
      <div className="label-eyebrow">{isEdit ? "Edit a listing" : "Create a listing"}</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">{isEdit ? "Edit your item." : "Sell an item."}</h1>
      <p className="mt-3 text-sm text-neutral-500 max-w-xl">
        {isEdit
          ? "Update any detail — photos, price, sizes, colours, authenticity or whether it's public or private. Changes go live as soon as you save."
          : "Every piece on Your Classic Style meets a near-excellent bar: honest grades, clear photos and close-ups of any flaws. It's why buyers trust what they order."}
      </p>

      <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="md:col-span-2 hairline p-6" data-testid="category-block">
          <div className="flex items-center gap-2">
            <Archive size={15} />
            <span className="label-eyebrow">Category</span>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {CATEGORIES.map((c) => (
              <button key={c.key} type="button" data-testid={`category-${c.key}`}
                onClick={() => changeCategory(c.key)}
                className="text-left hairline p-4 transition-colors"
                style={form.category === c.key ? { borderColor: "#1E3A2B", background: "rgba(30,58,43,0.05)" } : {}}>
                <div className="font-display text-base">
                  {c.key === "fabric_archives" ? <CategoryName /> : c.label}
                </div>
                <p className="mt-1 text-xs text-neutral-500">
                  {c.key === "fabric_archives"
                    ? "Upscale sewing fabric, lace, notions, heirloom & cultural textiles."
                    : c.key === "home_decor"
                    ? "Vintage cookware, dinnerware, utensils and table linens."
                    : "Pre-loved and vintage apparel, footwear and accessories."}
                </p>
              </button>
            ))}
          </div>
          {isFabric && (
            <div className="mt-5" data-testid="fabric-type-row">
              <div className="label-eyebrow mb-2">Which fabric category?</div>
              <div className="flex flex-wrap gap-2">
                {FABRIC_TYPES.map((ft) => (
                  <button key={ft} type="button" data-testid={`fabric-type-${ft}`}
                    onClick={() => set({ product_type: ft, measurements: {}, garment_type: "", size: "", waist: "", inseam: "" })}
                    className="px-3 py-2 hairline text-xs transition-colors"
                    style={form.product_type === ft ? { borderColor: "#1E3A2B", background: "#1E3A2B", color: "#fff" } : {}}>
                    {FABRIC_TYPE_LABELS[ft]}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-neutral-500">Pick the category your item belongs to — this is where buyers will find it.</p>
            </div>
          )}
          {isHomeDecor && (
            <div className="mt-5 space-y-4" data-testid="home-decor-type-row">
              <div>
                <div className="label-eyebrow mb-2">Which home decor category?</div>
                <div className="flex flex-wrap gap-2">
                  {HOME_DECOR_CATEGORIES.map((cat) => (
                    <button key={cat} type="button" data-testid={`hd-type-${cat}`}
                      onClick={() => set({ product_type: cat, garment_type: "", measurements: {} })}
                      className="px-3 py-2 hairline text-xs transition-colors"
                      style={form.product_type === cat ? { borderColor: "#1E3A2B", background: "#1E3A2B", color: "#fff" } : {}}>
                      {HOME_DECOR_CATEGORY_LABELS[cat]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-2">What is it?</div>
                <div className="flex flex-wrap gap-2" data-testid="hd-item-row">
                  {(HOME_DECOR_ITEMS[form.product_type] || []).map(([k, l]) => (
                    <button key={k} type="button" data-testid={`hd-item-${k}`}
                      onClick={() => set({ garment_type: form.garment_type === k ? "" : k })}
                      className="px-3 py-2 hairline text-xs transition-colors"
                      style={form.garment_type === k ? { borderColor: "#1E3A2B", background: "#1E3A2B", color: "#fff" } : {}}>
                      {l}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-neutral-500">Pick the item type — buyers filter by this in the Home Decor sidebar.</p>
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-2 hairline p-6">
          <div className="flex items-center gap-2">
            <Camera size={15} />
            <span className="label-eyebrow">Photos — at least 3 (front, back, detail)</span>
          </div>
          <div className="mt-3">
            <PhotoUpload paths={form.image_paths} onChange={(p) => set({ image_paths: p })} testid="photo-upload" cover />
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            Shoot in daylight against a plain background. Blurry or dim photos get a listing sent back.
          </p>
        </div>

        <div className="md:col-span-2 hairline p-6" data-testid="quality-block">
          <div className="flex items-center gap-2">
            <BadgeCheck size={15} style={{ color: "#1E3A2B" }} />
            <span className="label-eyebrow">Condition & required close-ups</span>
          </div>

          <div className="mt-5 grid md:grid-cols-2 gap-6">
            <Field label="Condition grade">
              <Select options={CONDITION_GRADES} labels={GRADE_LABELS} value={form.condition_grade}
                onChange={(v) => set({ condition_grade: v })} testid="input-grade" />
              <p className="mt-2 text-xs text-neutral-500" data-testid="grade-hint">{GRADE_HINTS[form.condition_grade]}</p>
              <p className="mt-1 text-xs" style={{ color: "#1E3A2B" }}>We only accept Good condition or better.</p>
            </Field>
            {(() => {
              const dept = apparelDept(form.gender, form.age_group);
              if (isHomeDecor) return null;
              if (form.product_type === "pattern") {
                const catActive = PATTERN_CATEGORIES.some(([k]) => k === form.garment_type);
                const wearerActive = (w) => !catActive && form.age_group === w.age && (w.sexed || form.gender === w.gender);
                const pickWearer = (w) => set({ garment_type: catActive ? "" : form.garment_type, age_group: w.age, gender: w.sexed ? "unisex" : w.gender, size: "" });
                const isSexed = !catActive && ["teen", "kid", "infant"].includes(form.age_group);
                const pickCat = (k) => set({ garment_type: k, gender: "unisex", age_group: "adult", size: "" });
                const sizeList = form.size ? form.size.split(",").map((x) => x.trim()).filter(Boolean) : [];
                const toggleSize = (s) => {
                  const next = sizeList.includes(s) ? sizeList.filter((x) => x !== s) : [...sizeList, s];
                  set({ size: next.join(", ") });
                };
                const inCls = "w-full hairline p-3 text-sm bg-white";
                const btn = (label, active, onClick, tid) => (
                  <button type="button" key={tid} data-testid={tid} data-active={active} onClick={onClick}
                    className="chip" style={active ? { background: "#1E3A2B", color: "#fff", borderColor: "#1E3A2B" } : {}}>{label}</button>);
                return (
                  <div className="space-y-5" data-testid="pattern-fields">
                    <Field label="Who's the pattern for?">
                      <div className="flex flex-wrap gap-2" data-testid="pattern-wearer">
                        {PATTERN_WEARERS.map((w) => btn(w.label, wearerActive(w), () => pickWearer(w), `pat-wearer-${w.key}`))}
                      </div>
                      {isSexed && (
                        <div className="flex flex-wrap gap-2 mt-2" data-testid="pattern-sex">
                          {btn("Girls", form.gender === "women", () => set({ gender: "women" }), "pat-sex-girls")}
                          {btn("Boys", form.gender === "men", () => set({ gender: "men" }), "pat-sex-boys")}
                          {btn("Unisex", form.gender === "unisex", () => set({ gender: "unisex" }), "pat-sex-unisex")}
                        </div>
                      )}
                    </Field>
                    {!catActive && (
                      <Field label="Garment the pattern makes (optional)">
                        <div className="flex flex-wrap gap-2" data-testid="pattern-clothing">
                          {PATTERN_CLOTHING.map(([k, l]) =>
                            btn(l, form.garment_type === k, () => set({ garment_type: form.garment_type === k ? "" : k }), `pat-cloth-${k}`))}
                        </div>
                      </Field>
                    )}
                    <Field label="Or a home / other pattern">
                      <div className="flex flex-wrap gap-2" data-testid="pattern-category">
                        {PATTERN_CATEGORIES.map(([k, l]) => btn(l, form.garment_type === k, () => pickCat(k), `pat-cat-${k}`))}
                      </div>
                    </Field>
                    {!catActive && (
                      <Field label="Pattern size(s) on the envelope">
                        <div className="flex flex-wrap gap-2" data-testid="pattern-size">
                          {[...PATTERN_LETTER_SIZES, ...PATTERN_NUMERIC_SIZES].map((s) =>
                            btn(s, sizeList.includes(s), () => toggleSize(s), `pat-size-${s}`))}
                        </div>
                        <p className="mt-1 text-xs text-neutral-500">Tap every size the envelope includes — buyers who wear any of them will find it.</p>
                      </Field>
                    )}
                    {!catActive && (
                      <Field label="Finished size — width & length (optional)">
                        <div className="grid grid-cols-2 gap-3">
                          <input className={inCls} data-testid="input-pattern-width" placeholder="Width, e.g. 42 in chest"
                            value={form.pattern_width} onChange={(e) => set({ pattern_width: e.target.value })} />
                          <input className={inCls} data-testid="input-pattern-length" placeholder="Length, e.g. 30 in"
                            value={form.pattern_length} onChange={(e) => set({ pattern_length: e.target.value })} />
                        </div>
                        <p className="mt-1 text-xs text-neutral-500">Helpful for men's patterns — buyers can search by finished chest width or garment length.</p>
                      </Field>
                    )}
                    <Field label="Difficulty (optional)">
                      <div className="flex flex-wrap gap-2" data-testid="pattern-difficulty">
                        {PATTERN_DIFFICULTY.map(([k, l]) =>
                          btn(l, form.difficulty === k, () => set({ difficulty: form.difficulty === k ? "" : k }), `pat-diff-${k}`))}
                      </div>
                    </Field>
                    <Field label="Pattern brand (optional)">
                      <input className={inCls} data-testid="input-pattern-brand" list="pattern-brands"
                        placeholder="e.g. Simplicity, McCall's, Butterick, Vogue"
                        value={form.brand} onChange={(e) => set({ brand: e.target.value })} />
                      <datalist id="pattern-brands">
                        {["Simplicity", "McCall's", "Butterick", "Vogue", "Kwik Sew", "Burda", "New Look"].map((b) => <option key={b} value={b} />)}
                      </datalist>
                      <p className="mt-1 text-xs text-neutral-500">Tip: include the pattern number in the title, e.g. "Simplicity 8234".</p>
                    </Field>
                  </div>
                );
              }
              if (form.product_type === "fabric") {
                const m = form.measurements || {};
                const setFab = (patch) => {
                  const mm = { ...m, ...patch };
                  const L = mm.length ? `${mm.length} ${mm.length_unit || "yd"}` : "";
                  const W = mm.width ? `${mm.width} ${mm.width_unit || "in"}` : "";
                  const size = [L && `${L} long`, W && `${W} wide`].filter(Boolean).join(" × ");
                  set({ measurements: mm, size });
                };
                const inCls = "w-full hairline p-3 text-sm bg-white";
                return (
                  <Field label="Fabric measurements">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.1em] text-neutral-500 mb-1">Length</div>
                        <div className="flex gap-2">
                          <input type="number" min="0" step="0.1" className={inCls} data-testid="input-fabric-length"
                            placeholder="e.g. 2" value={m.length || ""} onChange={(e) => setFab({ length: e.target.value })} />
                          <select className={inCls} style={{ maxWidth: 110 }} data-testid="input-fabric-length-unit"
                            value={m.length_unit || "yd"} onChange={(e) => setFab({ length_unit: e.target.value })}>
                            <option value="yd">yards</option><option value="m">metres</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.1em] text-neutral-500 mb-1">Width</div>
                        <div className="flex gap-2">
                          <input type="number" min="0" step="0.5" className={inCls} data-testid="input-fabric-width"
                            placeholder="e.g. 44" value={m.width || ""} onChange={(e) => setFab({ width: e.target.value })} />
                          <select className={inCls} style={{ maxWidth: 110 }} data-testid="input-fabric-width-unit"
                            value={m.width_unit || "in"} onChange={(e) => setFab({ width_unit: e.target.value })}>
                            <option value="in">inches</option><option value="cm">cm</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="text-[11px] uppercase tracking-[0.1em] text-neutral-500 mb-1">Cut / piece type</div>
                      <select className={inCls} data-testid="input-fabric-cut"
                        value={m.cut || ""} onChange={(e) => setFab({ cut: e.target.value })}>
                        <option value="">Select…</option>
                        <option value="continuous cut">Continuous cut (by the yard/metre)</option>
                        <option value="full bolt">Full bolt</option>
                        <option value="fat quarter">Fat quarter</option>
                        <option value="remnant">Remnant / offcut</option>
                        <option value="panel">Panel / precut</option>
                      </select>
                    </div>
                    <p className="mt-2 text-xs text-neutral-500" data-testid="fabric-size-preview">
                      Buyers see the size as: <strong>{form.size || "add length & width"}</strong>{m.cut ? ` · ${m.cut}` : ""}
                    </p>
                  </Field>
                );
              }
              const bottom = form.product_type === "apparel" && usesWaistLength(dept, form.garment_type);
              if (bottom) return (
                <>
                  <Field label="Waist">
                    <Select options={["", ...WAIST_SIZES[dept]]} value={form.waist} onChange={(v) => set({ waist: v })} testid="input-waist" />
                  </Field>
                  <Field label="Length (inseam)">
                    <Select options={["", ...INSEAM_SIZES[dept]]} value={form.inseam} onChange={(v) => set({ inseam: v })} testid="input-inseam" />
                  </Field>
                </>
              );
              if (isTies) return null;
              if (isHandbags) return (
                <Field label="Bag size"><Select options={["", ...HANDBAG_SIZES]} labels={HANDBAG_SIZE_LABELS} value={form.size} onChange={(v) => set({ size: v })} testid="input-size" /></Field>
              );
              return (
                <Field label={form.product_type === "shoes" ? "Shoe size (US)" : form.product_type === "apparel" ? "Size" : ACCESSORY_SIZES[form.product_type] ? ACCESSORY_SIZES[form.product_type].label : "Size (label / tag)"}>
                  {form.product_type === "shoes" ? (
                    <Select options={["", ...shoeSizesFor(form.gender, form.age_group)]}
                      value={form.size} onChange={(v) => set({ size: v })} testid="input-size" />
                  ) : form.product_type === "apparel" ? (
                    <Select options={["", ...clothingSizesFor(form.gender, form.age_group)]}
                      value={form.size} onChange={(v) => set({ size: v })} testid="input-size" />
                  ) : ACCESSORY_SIZES[form.product_type] ? (
                    <Select options={["", ...ACCESSORY_SIZES[form.product_type].options]}
                      value={form.size} onChange={(v) => set({ size: v })} testid="input-size" />
                  ) : (
                    <input className="w-full hairline p-3 text-sm bg-white" data-testid="input-size"
                      placeholder="e.g. M, 32×34, US 9" value={form.size} onChange={(e) => set({ size: e.target.value })} />
                  )}
                </Field>
              );
            })()}
          </div>

          <div className="mt-6">
            <div className="label-eyebrow">Label close-up (optional)</div>
            <p className="text-xs text-neutral-500 mt-1">
              {isFabric
                ? "A clear shot of the selvedge, bolt-end or maker label (or the fibre-content tag)."
                : "A clear shot of the brand / size / care label so buyers can read every line. Optional, but it builds trust."}
            </p>
            <div className="mt-3">
              <PhotoUpload paths={form.label_paths} onChange={(p) => set({ label_paths: p })}
                cta="Upload label close-up" testid="label-upload" />
            </div>
          </div>

          <div className="mt-6">
            <div className="label-eyebrow">Flaw close-ups</div>
            <p className="text-xs text-neutral-500 mt-1">Photograph every mark, pill, stain, scuff or repair. Honesty here prevents claims later.</p>
            <div className="mt-3">
              <PhotoUpload paths={form.flaw_paths} onChange={(p) => set({ flaw_paths: p })}
                cta="Upload flaw close-ups" testid="flaw-upload" />
            </div>
            <div className="mt-3">
              <Check label="No visible flaws — this item is free of marks, wear and repairs"
                checked={form.no_flaws} onChange={(v) => set({ no_flaws: v })} testid="no-flaws-check" />
            </div>
          </div>
        </div>

        {!isPattern && form.product_type !== "fabric" && !isHomeDecor && (
        <div className="md:col-span-2 hairline p-6" data-testid="measurements-block">
          <div className="flex items-center gap-2">
            <Ruler size={15} />
            <span className="label-eyebrow">Exact measurements</span>
          </div>
          <p className="text-xs text-neutral-500 mt-1">Lay the piece flat and measure edge to edge. All optional — but exact measurements mean fewer returns.</p>
          <div className="mt-4 grid md:grid-cols-3 gap-4">
            {spec.map(([k, lbl]) => (
              <Field key={k} label={lbl}>
                <input className="w-full hairline p-3 text-sm bg-white" data-testid={`measure-${k}`}
                  placeholder='e.g. 21 in / 53 cm' value={form.measurements[k] || ""}
                  onChange={(e) => setMeasure(k, e.target.value)} />
              </Field>
            ))}
          </div>
        </div>
        )}

        <div className="md:col-span-2 hairline p-6" data-testid="disclosures-block">
          <div className="label-eyebrow">Disclosures</div>
          <p className="text-xs text-neutral-500 mt-1">Tick everything that's true. Buyers rely on these before they order.</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-8">
            <Check label="From a smoke-free home" checked={form.smoke_free} onChange={(v) => set({ smoke_free: v })} testid="smoke-free-check" />
            <Check label="From a pet-free home" checked={form.pet_free} onChange={(v) => set({ pet_free: v })} testid="pet-free-check" />
            <Check label="No odor — laundered / freshly cleaned" checked={form.odor_free} onChange={(v) => set({ odor_free: v })} testid="odor-free-check" />
            <Check label="Item has been altered / tailored / repaired" checked={form.altered} onChange={(v) => set({ altered: v })} testid="altered-check" />
          </div>
          {form.altered && (
            <div className="mt-3">
              <input className="w-full hairline p-3 text-sm bg-white" data-testid="alteration-note"
                placeholder="Describe the alteration (e.g. hem taken up 2 in, zip replaced)"
                value={form.alteration_note} onChange={(e) => set({ alteration_note: e.target.value })} />
            </div>
          )}
        </div>

        <div className="md:col-span-2 hairline p-6" style={{ borderColor: "#2563EB" }} data-testid="authenticity-block">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} style={{ color: "#2563EB" }} />
            <span className="label-eyebrow" style={{ color: "#2563EB" }}>Authenticity — protects you and your buyer</span>
          </div>
          <div className="mt-4 grid md:grid-cols-3 gap-3">
            {AUTH_CLAIMS.map((c) => (
              <button key={c.value} type="button" data-testid={`claim-${c.value}`}
                onClick={() => set({ authenticity_claim: c.value, brand: c.value === "unbranded" ? "" : form.brand })}
                className="text-left hairline p-4 transition-colors"
                style={claim === c.value
                  ? { borderColor: "#2563EB", background: "rgba(37,99,235,0.05)" }
                  : {}}>
                <div className="font-display text-base">{c.title}</div>
                <p className="mt-2 text-xs text-neutral-500 leading-relaxed">{c.blurb}</p>
              </button>
            ))}
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <Field label={brandLocked ? "Brand (locked — no brand claim)" : claim === "replica" ? "Resembles brand" : "Brand"}>
              <div className="relative">
                <input disabled={brandLocked} data-testid="input-brand"
                  placeholder={brandLocked ? "Not applicable" : "e.g. Levi's, Guess, Coach"}
                  className="w-full hairline p-3 text-sm bg-white disabled:bg-neutral-100 disabled:text-neutral-400"
                  value={form.brand} onChange={(e) => set({ brand: e.target.value })} />
                {brandLocked && <Lock size={13} className="absolute right-3 top-3.5 text-neutral-400" />}
              </div>
            </Field>
            <Field label="Era Style">
              <input className="w-full hairline p-3 text-sm bg-white" data-testid="input-era-text"
                placeholder="Describe the era & style — e.g. 1970s bohemian, Y2K streetwear, Art Deco glamour"
                value={form.era} onChange={(e) => set({ era: e.target.value })} />
              <p className="mt-1 text-xs text-neutral-500">Tell buyers the era and the vibe in your own words.</p>
            </Field>
          </div>

          {claim === "authentic" && (
            <div className="mt-6" data-testid="proof-section">
              <div className="label-eyebrow">Proof close-ups (optional)</div>
              <ul className="mt-2 grid sm:grid-cols-2 gap-1 text-xs text-neutral-500">
                {PROOF_HINTS.map((h) => <li key={h}>· {h}</li>)}
              </ul>
              <p className="mt-2 text-xs" style={{ color: "#16A34A" }}>
                Adding clear tag, label and stitching close-ups helps buyers trust your authentic claim.{" "}
                <Link to="/proof-guide" className="underline" data-testid="sell-proof-guide-link">See the proof guide →</Link>
              </p>
              <div className="mt-3">
                <PhotoUpload paths={form.proof_paths} onChange={(p) => set({ proof_paths: p })}
                  cta="Upload tag, label & stitching close-ups" testid="proof-upload" />
              </div>
              <p className="mt-3 flex items-start gap-2 text-xs text-neutral-500">
                <Info size={13} className="mt-0.5 shrink-0" />
                Your item goes live right away with a verified-authentic badge. You are responsible for the claim —
                listing a knockoff as an original gets the item pulled and costs you a strike, and three strikes
                suspends selling.
              </p>
            </div>
          )}
          {claim === "replica" && (
            <p className="mt-4 text-xs" style={{ color: "#EA580C" }}>
              Disclosed replicas stay live with a clear amber badge and are hidden from buyers who filter replicas out.
              {pricing && ` They cannot be listed above $${pricing.replica_price_cap.toFixed(0)}.`}
            </p>
          )}
          {replicaOverCap && (
            <p className="mt-2 text-xs font-medium" style={{ color: "#E11D48" }} data-testid="replica-cap-warning">
              ${priceNum.toFixed(2)} is over the ${pricing.replica_price_cap.toFixed(0)} replica cap — lower the price
              or list it as authentic with proof.
            </p>
          )}
          {authFee > 0 && (
            <div className="mt-4 hairline p-4" style={{ borderColor: "#16A34A" }} data-testid="auth-fee-notice">
              <div className="label-eyebrow" style={{ color: "#16A34A" }}>Designer authentication · ${authFee.toFixed(2)}</div>
              <p className="mt-2 text-xs text-neutral-600">
                Items over ${pricing.authentication_required_above.toFixed(0)} get a deeper authentication pass. The fee
                is deducted from your payout only when the item sells — never upfront, never if it doesn't sell.{" "}
                <Link to="/pricing" className="underline">See all fees</Link>
              </p>
            </div>
          )}
        </div>

        <Field label="Title">
          <input required className="w-full hairline p-3 text-sm bg-white" data-testid="input-title"
            value={form.title} onChange={(e) => set({ title: e.target.value })} />
        </Field>
        <Field label="Price (USD)">
          <input required type="number" step="0.01" min="0.5" className="w-full hairline p-3 text-sm bg-white"
            data-testid="input-price" value={form.price} onChange={(e) => set({ price: e.target.value })} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Description">
            <textarea rows={4} className="w-full hairline p-3 text-sm bg-white" data-testid="input-description"
              value={form.description} onChange={(e) => set({ description: e.target.value })} />
          </Field>
        </div>
        {!isFabric && !isHomeDecor && (
          <div className="md:col-span-2">
            <div className="label-eyebrow mb-2">Who's it for?</div>
            <div className="flex flex-wrap gap-2" data-testid="sell-dept-quickpick">
              {[
                { key: "women", label: "Women", gender: "women", age: "adult" },
                { key: "men", label: "Men", gender: "men", age: "adult" },
                { key: "girls", label: "Girls", gender: "women", age: "kid" },
                { key: "boys", label: "Boys", gender: "men", age: "kid" },
                { key: "kids", label: "Kids · Unisex", gender: "unisex", age: "kid" },
              ].map((d) => {
                const active = form.gender === d.gender && form.age_group === d.age;
                return (
                  <button key={d.key} type="button" data-testid={`sell-dept-${d.key}`} data-active={active}
                    onClick={() => set({ gender: d.gender, age_group: d.age, garment_type: "", size: "", waist: "", inseam: "" })}
                    className="chip" style={active ? { background: "#1E3A2B", color: "#fff", borderColor: "#1E3A2B" } : {}}>
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {!isFabric && !isHomeDecor && (() => {
          const isKid = form.age_group !== "adult";
          return (
            <Field label={isKid ? "Boy / Girl" : "Gender"}>
              <Select options={GENDERS}
                labels={isKid ? { women: "Girls", men: "Boys", unisex: "Unisex" } : { women: "Women", men: "Men", unisex: "Unisex" }}
                value={form.gender}
                onChange={(v) => set({ gender: v, garment_type: "", size: "", waist: "", inseam: "" })}
                testid="input-gender" />
            </Field>
          );
        })()}
        {!isFabric && !isHomeDecor && <Field label="Age group"><Select options={AGES} value={form.age_group} onChange={(v) => set({ age_group: v, garment_type: "", size: "", waist: "", inseam: "" })} testid="input-age" /></Field>}
        {!isFabric && !isHomeDecor && (
          <Field label="Type">
            <Select options={PRODUCT_TYPES}
              value={form.product_type} onChange={(v) => set({ product_type: v, measurements: {}, garment_type: "", size: "", waist: "", inseam: "" })} testid="input-type" />
          </Field>
        )}
        {!isFabric && form.product_type === "apparel" && (
          <Field label="Garment type">
            <Select options={["", ...garmentTypesFor(form.gender, form.age_group).map((g) => g.key)]}
              labels={garmentTypesFor(form.gender, form.age_group).reduce((a, g) => ({ ...a, [g.key]: g.label }), {})}
              value={form.garment_type} onChange={(v) => set({ garment_type: v, size: "", waist: "", inseam: "", measurements: {} })} testid="input-garment" />
          </Field>
        )}
        {isPattern || isWatches ? null : isFabric ? (
          <Field label="Colours (select all that apply)">
            <div className="flex flex-wrap gap-2" data-testid="input-color-multi">
              {COLORS.map((cName) => {
                const selected = (form.color ? form.color.split(",").map((s) => s.trim()) : []);
                const on = selected.includes(cName);
                return (
                  <button type="button" key={cName} data-testid={`color-chip-${cName}`}
                    onClick={() => {
                      const next = on ? selected.filter((x) => x !== cName) : [...selected, cName];
                      set({ color: next.join(", ") });
                    }}
                    className="px-3 py-1.5 hairline text-xs capitalize transition-colors"
                    style={on ? { background: "#1E3A2B", color: "#fff", borderColor: "#1E3A2B" } : {}}>
                    {cName}
                  </button>
                );
              })}
            </div>
          </Field>
        ) : (
          <Field label="Color"><Select options={COLORS} value={form.color} onChange={(v) => set({ color: v })} testid="input-color" /></Field>
        )}
        {isWatches && (
          <>
            <Field label="Band"><Select options={["", ...WATCH_BANDS]} labels={WATCH_BAND_LABELS} value={form.band} onChange={(v) => set({ band: v })} testid="input-band" /></Field>
            <Field label="Movement"><Select options={["", ...WATCH_MOVEMENTS]} labels={WATCH_MOVEMENT_LABELS} value={form.movement} onChange={(v) => set({ movement: v })} testid="input-movement" /></Field>
            <Field label="Dial colour"><Select options={["", ...WATCH_DIAL_COLORS]} value={form.dial_color} onChange={(v) => set({ dial_color: v })} testid="input-dial" /></Field>
            <Field label="Water resistance"><Select options={["", ...WATCH_WATER_RESISTANCE]} value={form.water_resistance} onChange={(v) => set({ water_resistance: v })} testid="input-water-resistance" /></Field>
          </>
        )}
        {isJewelry && (
          <>
            <Field label="Metal"><Select options={["", ...JEWELRY_METALS]} labels={JEWELRY_METAL_LABELS} value={form.metal} onChange={(v) => set({ metal: v })} testid="input-metal" /></Field>
            <Field label="Hallmark / purity"><Select options={["", ...JEWELRY_HALLMARKS]} labels={JEWELRY_HALLMARK_LABELS} value={form.hallmark} onChange={(v) => set({ hallmark: v })} testid="input-hallmark" /></Field>
            <Field label="Stone"><Select options={["", ...JEWELRY_STONES]} labels={JEWELRY_STONE_LABELS} value={form.stone} onChange={(v) => set({ stone: v })} testid="input-stone" /></Field>
            {form.product_type === "rings" && (
              <Field label="Resizing">
                <Check label="This ring can be resized" checked={form.resizable} onChange={(v) => set({ resizable: v })} testid="input-resizable" />
              </Field>
            )}
          </>
        )}
        {!isFabric && !isWatches && (
          <Field label="Material (optional)">
            <Select options={["", ...MATERIALS]} value={form.material} onChange={(v) => set({ material: v })} testid="input-material" />
          </Field>
        )}
        {isWallets && (
          <Field label="Wallet style"><Select options={["", ...WALLET_STYLES]} labels={WALLET_STYLE_LABELS} value={form.garment_type} onChange={(v) => set({ garment_type: v })} testid="input-wallet-style" /></Field>
        )}
        {isWallets && (
          <Field label="Card slots"><Select options={["", ...WALLET_CARD_SLOTS]} value={form.card_slots} onChange={(v) => set({ card_slots: v })} testid="input-card-slots" /></Field>
        )}
        {isHandbags && (
          <Field label="Bag style"><Select options={["", ...HANDBAG_STYLES]} labels={HANDBAG_STYLE_LABELS} value={form.garment_type} onChange={(v) => set({ garment_type: v })} testid="input-bag-style" /></Field>
        )}
        {isTies && (
          <Field label="Tie style"><Select options={["", ...TIE_STYLES]} labels={TIE_STYLE_LABELS} value={form.garment_type} onChange={(v) => set({ garment_type: v })} testid="input-tie-style" /></Field>
        )}
        {!isPattern && !isWatches && !isWallets && !isHandbags && !isTies && <Field label="Pattern"><Select options={PATTERNS} value={form.pattern} onChange={(v) => set({ pattern: v })} testid="input-pattern" /></Field>}
        {!isFabric && !isWallets && !isHandbags && !isTies && !isHomeDecor && <Field label="Style"><Select options={STYLES} value={form.style} onChange={(v) => set({ style: v })} testid="input-style" /></Field>}

        <div className="md:col-span-2 hairline p-6" data-testid="package-block">
          <div className="flex items-center gap-2">
            <Package size={15} />
            <span className="label-eyebrow">Package size (for shipping)</span>
          </div>
          <p className="text-xs text-neutral-600 mt-2 leading-relaxed">
            Pick the box this ships in. Buyers get live carrier rates based on this exact size — the smaller the box,
            the cheaper their shipping, so choosing right helps your item sell.
          </p>
          <div className="mt-3 max-w-md">
            <Select options={PACKAGE_SIZES} labels={PACKAGE_SIZE_LABELS} value={form.package_size}
              onChange={(v) => set({ package_size: v })} testid="input-package-size" />
            <p className="mt-2 text-xs text-neutral-500">{PACKAGE_SIZE_HINTS[form.package_size]}</p>
          </div>
          <div className="mt-4 pt-4 border-t border-black/10">
            <Check testid="tradeable-ack" checked={form.tradeable} onChange={(v) => set({ tradeable: v })}
              label="Open to trades — show a green Trade badge so buyers can propose a barter for this item. You can turn this on or off anytime." />
          </div>
        </div>

        <div className="md:col-span-2 hairline p-6" data-testid="private-block" style={{ borderColor: "#1E3A2B" }}>
          <div className="flex items-center gap-2">
            <Lock size={15} style={{ color: "#1E3A2B" }} />
            <span className="label-eyebrow" style={{ color: "#1E3A2B" }}>Private collection (optional)</span>
          </div>
          <p className="text-xs text-neutral-600 mt-2 leading-relaxed">
            Hide this piece from public browsing. Only shoppers you approve (from your favourites) can view and buy it.
            A private collection holds up to 20 items. <strong>Sellers pay a 3% fee on private sales</strong> — this
            is waived on the Pro plan.
          </p>
          <div className="mt-3">
            <Check testid="private-ack" checked={form.private} onChange={(v) => set({ private: v })}
              label="List this item as private (hidden from public browse)." />
          </div>
        </div>

        <div className="md:col-span-2 flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="publish-btn">
            {saving ? (isEdit ? "Saving…" : "Publishing…") : (isEdit ? "Save changes" : "Publish listing")}
          </button>
        </div>
      </form>

      {bumpModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bump-modal">
          <div className="bg-white hairline max-w-md w-full p-7">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} style={{ color: "#1E3A2B" }} />
              <span className="label-eyebrow">Feature on the homepage</span>
            </div>
            <h2 className="font-display text-2xl tracking-tight mt-3">Your listing is live.</h2>
            <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
              Give it a head start. Featuring pins your item to the <strong>homepage “For Sale” grid</strong>,
              so it's the first thing shoppers see — then it returns to the normal rotation. Pick how long:
            </p>
            <div className="mt-4">
              <BumpTierPicker listingId={bumpModal.id} tiers={bumpBoost.home_bump_tiers}
                onCancel={skipBump} cancelLabel="Not now" testidPrefix="bump" />
            </div>
            <p className="mt-3 text-[0.62rem] text-neutral-400 text-center">Secure Stripe checkout · you can also feature it later from your Dashboard.</p>
          </div>
        </div>
      )}
    </div>
  );
}
